import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { get } from 'lodash';
import { type ReactNode, useRef, useState } from 'react';

import { HttpError } from '@revelio/data-access';

import { showAlertDialog } from '../components/alert-dialog';
import { useKibanaLogger } from '../hooks/kibana-logger/kibana-logger';
import { UrqlAndTanstackErrorMessage } from '../ui/urql-and-tanstack-error-message/urql-and-tanstack-error-message';

// Register custom meta types
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: {
      skipGlobalErrorHandling?: boolean;
      expectedErrorStatuses?: number[]; // Errors with these statuses are skipped by the global handler.
    };
    mutationMeta: {
      skipGlobalErrorHandling?: boolean;
    };
  }
}

export class TanstackQueryError extends Error {
  constructor(
    message: string,
    public requestId: string
  ) {
    super(message);
    this.name = 'TanstackQueryError';
  }
}

export const TanstackQueryProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { kibanaLogger } = useKibanaLogger();
  const kibanaLoggerRef = useRef(kibanaLogger);
  kibanaLoggerRef.current = kibanaLogger;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            if (mutation.meta?.skipGlobalErrorHandling) {
              return;
            }

            const logger = kibanaLoggerRef.current;
            if (logger) {
              const mutationKey = JSON.stringify(
                mutation.options.mutationKey ?? 'unknown'
              );
              const message = `Mutation Error: ${error.message}\n\nmutationKey: ${mutationKey}`;

              logger.error(message, {
                'request-id': get(error, 'requestId'),
                url: window.location.href,
              });
            }
          },
        }),
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (query.meta?.skipGlobalErrorHandling) {
              return;
            }

            const expectedErrorStatuses = query.meta?.expectedErrorStatuses;
            if (
              expectedErrorStatuses &&
              error instanceof HttpError &&
              expectedErrorStatuses.includes(error.status)
            ) {
              return;
            }

            const requestID = get(error, 'requestId');
            const logger = kibanaLoggerRef.current;
            if (logger) {
              const queryKey = JSON.stringify(query.queryKey);
              const message = `Query Error: ${error.message}\n\nqueryKey: ${queryKey}`;

              logger.error(message, {
                'request-id': requestID,
                url: window.location.href,
              });
            }

            showAlertDialog({
              alertType: 'actionless',
              isOpen: true,
              headerText: 'Sorry!',
              bodyContent: (
                <UrqlAndTanstackErrorMessage error={error} reqId={requestID} />
              ),
            });
          },
        }),
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              if (error instanceof HttpError && !error.isRetriableError()) {
                console.error('Non-retriable client error', error);
                return false;
              }

              console.error(`Tanstack Query Error (${failureCount})`, error);
              return failureCount < 2;
            },
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
